/// <reference types="@types/google.maps" />
import React, { useEffect, useRef } from "react";
import { Wrapper } from "@googlemaps/react-wrapper";

interface BranchLocation {
    ou_code: string;
    ou_name: string;
    lat: string | number | null;
    lon: string | number | null;
    color?: string;
}

interface Props {
    branches: BranchLocation[];
    legend: Record<string, string>;
    legendLevel: string;
    onBranchClick?: (ouCode: string) => void;
}

const containerStyle: React.CSSProperties = {
    width: "100%",
    height: "100%",
};

const MapComponent: React.FC<Props> = ({ branches, legend, legendLevel, onBranchClick }) => {
    const ref = useRef<HTMLDivElement | null>(null);
    const mapRef = useRef<google.maps.Map | null>(null);
    const markersRef = useRef<google.maps.Marker[]>([]);

    useEffect(() => {
        if (!ref.current || !window.google || mapRef.current) return;

        mapRef.current = new window.google.maps.Map(ref.current, {
            center: { lat: 20.5937, lng: 78.9629 },
            zoom: 5,
            mapTypeId: "roadmap",
            streetViewControl: false,
            fullscreenControl: true,
        });
    }, []);

    useEffect(() => {
        if (!mapRef.current) return;

        markersRef.current.forEach((m) => m.setMap(null));
        markersRef.current = [];

        const bounds = new google.maps.LatLngBounds();
        const infoWindow = new google.maps.InfoWindow();

        branches.forEach((b) => {
            if (!b.lat || !b.lon) return;

            const position = {
                lat: Number(b.lat),
                lng: Number(b.lon),
            };

            const pinColor = b.color || "#3498db";

            const svgMarker = {
                url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
                    <svg width="32" height="48" viewBox="0 0 24 36" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M12 0C7 0 3 4 3 9c0 7 9 17 9 17s9-10 9-17c0-5-4-9-9-9z"
                            fill="${pinColor}"
                            stroke="white"
                            stroke-width="1.5"
                        />
                        <circle cx="12" cy="9" r="3.5" fill="white"/>
                    </svg>
                `)}`,
                scaledSize: new google.maps.Size(32, 48),
                anchor: new google.maps.Point(16, 48),
            };

            const marker = new google.maps.Marker({
                position,
                map: mapRef.current!,
                title: `${b.ou_code} - ${b.ou_name}`,
                icon: svgMarker,
            });

            marker.addListener("click", () => {
                if (onBranchClick && b.ou_code) {
                    onBranchClick(b.ou_code);
                }

                infoWindow.setContent(
                    `<div>
                        <strong>${b.ou_name}</strong><br/>
                        Code: ${b.ou_code}
                    </div>`
                );
                infoWindow.open(mapRef.current, marker);
            });

            markersRef.current.push(marker);
            bounds.extend(position);
        });

        if (branches.length > 0) {
            mapRef.current.fitBounds(bounds);
        }
    }, [branches]);

    return (
        <div className="relative w-full h-full">
            <div ref={ref} style={containerStyle} />
            {Object.keys(legend).length > 0 && (
                <div className="absolute top-16 right-3 bg-white shadow-md border rounded p-3 text-xs max-h-64 overflow-auto">
                    <div className="font-semibold mb-2 capitalize">
                        {legendLevel}
                    </div>

                    {Object.entries(legend).map(([label, color]) => (
                        <div key={label} className="flex items-center gap-2 mb-1">
                            <span
                                className="w-3 h-3 rounded-sm border"
                                style={{ backgroundColor: color }}
                            />
                            <span>{label}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const BranchNetworkMap: React.FC<Props> = (props) => {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY!;

    return (
        <Wrapper apiKey={apiKey}>
            <MapComponent {...props} />
        </Wrapper>
    );
};

export default BranchNetworkMap;
